import { GoalsInput } from '@cpd-app/generated';

/**
 * CPD-661 items 8 and 9. THE CAREER GOAL IS THE ONLY GOAL THAT CASCADES.
 *
 * Jackson's rule, 23 Sep (CPD-661 comment 71067), replacing the one written
 * into `updateGoals$` on 22 Sep:
 *
 *   - Removing the career goal, or changing it to a different occupation,
 *     wipes the program, school, credential and license. A different career
 *     needs different education targets.
 *   - Removing any OTHER goal removes that goal and nothing else. The program
 *     is now chosen before the school, so dropping a school says nothing about
 *     the program; the credential and license are optional and stand alone.
 *
 * The rule lives here, applied in the one place every UI goal write passes
 * through, rather than as a sequence of removals dispatched by each caller.
 * That sequence is what CPD-668 caught: `GoalRemovalService.removeCareerGoal`
 * fired three writes in the same tick, each merged onto the SAME stale goals
 * (the store only updates on success), so the career-clearing write carried
 * the old school and program straight back, and whichever of the three the
 * server received last decided which goals survived.
 */

/** Every goal that hangs off the career goal, cleared. */
export const EDUCATION_GOALS_CLEARED: Readonly<
  Required<
    Pick<
      GoalsInput,
      | 'schoolId'
      | 'schoolProgramId'
      | 'schoolProgramEdLevelId'
      | 'certificationId'
      | 'licenseId'
      | 'credentialCtid'
      | 'credentialTitle'
    >
  >
> = {
  schoolId: null,
  schoolProgramId: null,
  schoolProgramEdLevelId: null,
  certificationId: null,
  licenseId: null,
  credentialCtid: null,
  credentialTitle: null
};

/**
 * Does writing `next` as the career goal cut the education goals loose?
 *
 * Yes when the career is cleared, or replaced by a DIFFERENT occupation. No
 * when the member sets the career goal they already hold (a re-save is not a
 * change), and no when there was no career goal on file: nothing was chosen
 * FOR a career that did not exist, so there is nothing to invalidate.
 *
 * Callers must only ask this for a write that names the career. Every other
 * goal write inherits an occupation it did not choose -- `updateGoals$`
 * resolves it from the page open behind it -- and must never be read as the
 * member changing career.
 */
export function careerGoalCutsEducation(
  storedOnetCode: string | null | undefined,
  nextOnetCode: string | null | undefined
): boolean {
  const stored = storedOnetCode || null;
  const next = nextOnetCode || null;
  if (next === null) {
    return true;
  }
  return stored !== null && stored !== next;
}
